import { captureCustomException } from 'App/Services/ErrorTracking/captureCustomException';

import { isWebView } from './deviceInfo';

let LS_OK = null;
let ERROR_REPORTED = false;

/**
 * Call this once at app startup to set LS_OK and lock in behavior.
 */
export function initStorageAccess() {
    if (LS_OK !== null) return LS_OK;

    try {
        const ls = window.localStorage;
        const key = '__probe_ls__';

        ls.setItem(key, '1');
        ls.removeItem(key);

        LS_OK = true;
    } catch (err) {
        LS_OK = false;

        if (!ERROR_REPORTED) {
            ERROR_REPORTED = true;

            const webView = isWebView();

            captureCustomException(err, 'LocalStorageNotAccessible', {
                is_webview: String(webView),
            });
        }
    }

    return LS_OK;
}

/**
 * Parses the localStorage value into an object.
 * @param {string} storageKey
 */
function getStorageObject(storageKey) {
    if (!LS_OK) return {};

    try {
        const raw = window.localStorage?.getItem(storageKey);

        return raw ? JSON.parse(raw) : {};
    } catch (error) {
        captureCustomException(error, 'LocalStorageReadFailed');

        return {};
    }
}

/**
 * Serializes and stores the object back into localStorage.
 * @param {string} storageKey
 * @param {object} obj
 */
function setStorageObject(storageKey, obj) {
    localStorage.setItem(storageKey, JSON.stringify(obj));
}

/**
 * Converts dot-notation path string to array.
 * @param {string} pathStr
 * @returns {string[]}
 */
function parsePath(pathStr) {
    return pathStr.split('.').filter(Boolean);
}

/**
 * Helper to deeply set a value in an object using a path array.
 * Modifies the target object directly.
 * @param {Object} target
 * @param {string[]} path
 * @param {*} value
 */
function applyNestedSet(target, path, value) {
    if (path.length === 0) return;

    let nested = target;

    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (!nested[key] || typeof nested[key] !== 'object') {
            nested[key] = {};
        }
        nested = nested[key];
    }

    nested[path[path.length - 1]] = value;
}

/**
 * Retrieves a deeply nested value using a dot-notation path.
 * @param {string} pathStr - e.g. 'push.pushStatus'
 * @param {string} storageKey
 * @returns {*} Value or undefined
 */
export function getStorageValue(storageKey, pathStr) {
    const obj = getStorageObject(storageKey);
    const path = parsePath(pathStr);

    return path.reduce(
        (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
        obj
    );
}

/**
 * Sets a deeply nested value using dot-notation path.
 * @param {string} storageKey
 * @param {string} pathStr - e.g. 'push.pushStatus'
 * @param {*} value
 */
export function setStorageValue(storageKey, pathStr, value) {
    const path = parsePath(pathStr);

    if (path.length === 0) {
        if (typeof value === 'object' && value !== null) {
            setStorageObject(storageKey, value);
        } else {
            console.warn('Root storage value must be an object');
        }

        return;
    }

    const obj = getStorageObject(storageKey);

    applyNestedSet(obj, path, value);
    setStorageObject(storageKey, obj);
}

/**
 * Gets multiple nested values from the same localStorage object.
 * Returns an object where keys are the requested paths and values are the resolved values.
 *
 * @param {string} storageKey - localStorage key (e.g. 'bcng_strg')
 * @param {string[]} pathList - array of dot-path strings (e.g. ['app.offerId', 'game.buildVersion'])
 * @returns {Record<string, any>} - e.g. { 'app.offerId': '123', 'game.buildVersion': 'v1.0.1' }
 */
export function getMultipleStorageValues(storageKey, pathList) {
    const obj = getStorageObject(storageKey);
    const results = {};

    for (const pathStr of pathList) {
        const path = parsePath(pathStr);

        results[pathStr] = path.reduce(
            (acc, key) =>
                acc && acc[key] !== undefined ? acc[key] : undefined,
            obj
        );
    }

    return results;
}

/**
 * Sets multiple nested values in one localStorage update.
 * Accepts an object where keys are dot-paths and values are the values to set.
 *
 * @param {string} storageKey - localStorage key (e.g. 'bcng_strg')
 * @param {Object.<string, any>} updates - e.g. { 'app.offerId': 'xyz', 'game.build': '1.2.3' }
 */
export function setMultipleStorageValues(storageKey, updates) {
    const obj = getStorageObject(storageKey);

    for (const pathStr in updates) {
        const value = updates[pathStr];
        const path = parsePath(pathStr);

        if (path.length > 0) {
            applyNestedSet(obj, path, value);
        }
    }

    setStorageObject(storageKey, obj);
}

/**
 * Checks for a value by key in localStorage and removes it if present.
 *
 * @param {string} key – the localStorage key to check & clear
 */
export const clearStorageValueByKey = (key) => {
    if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
    }
};

/**
 * Get a value from a flat (non-nested) Local Storage.
 * @param name {string}
 *
 * @return {string}
 */
export const getLocalStorageVal = (name) => {
    return localStorage?.getItem(name);
};

export const localStorageValueReset = (key, value) => {
    const localStorageValue = localStorage.getItem(key);

    !localStorageValue && localStorage.setItem(key, value);

    return localStorageValue;
};
